import {
  addQueryParams,
  buildUseQuery,
  removeQueryParams,
  Skeleton,
  useRouter,
} from '@main/core-ui';
import { SanitizeHtml } from '@main/pc-core-components';
import { endpoints, privacyCenterCustomizableMessages } from '@main/pc-types';
import TakeControlConfirm from '@main/privacy-center/src/components/TakeControlConfirm';
import ActiveDataSubject from '@main/privacy-center/src/containers/ActiveDataSubject';
import { selectActiveDataSubject } from '@main/privacy-center/src/containers/ActiveDataSubject/selectors';
import {
  selectCoreIdentifier,
  selectIsLoggedIn,
  selectIsLoggingIn,
  selectRequiredPrivacyCenter,
} from '@main/privacy-center/src/containers/App/selectors';
import { Login } from '@main/privacy-center/src/containers/Login';
import { useAuthorizationUrl } from '@main/privacy-center/src/containers/Login/generateAuthorizationUrl';
import {
  setSelectedWorkflowId,
  unsetSelectedAction,
  unsetSelectedWorkflowId,
} from '@main/privacy-center/src/containers/TokenLogin/slice';
import { buildUseDhMutation } from '@main/privacy-center/src/hooks';
import type { LanguageKey } from '@transcend-io/internationalization';
import { RequestAction } from '@transcend-io/privacy-types';
import React, { forwardRef, useState } from 'react';
import { useIntl } from 'react-intl';
import { useDispatch, useSelector } from 'react-redux';

import { usePartition } from '../../hooks/usePartition';
import { unsetActiveDataSubject } from '../ActiveDataSubject/slice';
import { writeOauthStateToLocalStorage } from '../TokenLogin/helpers';
import { DisplayWorkflows } from './DisplayWorkflows';
import { formToRequestIdentifiers } from './formToRequestIdentifiers';
import { takeControlMessages } from './messages';
import { selectTakeControlModalName } from './selectors';
import {
  ButtonWrapper,
  ClassOverrides,
  DataSubjectValueWrapper,
  DataSubjectWrapper,
  DescriptionWrapper,
  HeaderWrapper,
  ModalWrapper,
  SelectSubjectModalWrapper,
  StyledModal,
  TakeControlModalWrapper,
  TakeControlWrapper,
  TextWrapper,
  TitleWrapper,
} from './wrappers';
import { syncAllConsent, syncSaleOfInfoConsent } from './xdi';

const useMakeDataSubjectRequests = buildUseDhMutation(
  endpoints.makeDataSubjectRequests,
);

const usePrivacyCenterWorkflows = buildUseQuery(
  endpoints.privacyCenterWorkflows,
);

export interface TakeControlProps {
  /** Explicit onExited function with context of modal button ref */
  onExited: () => void;
}

/**
 * Allow the user to make a DSR
 */
export const WorkflowsTakeControl = forwardRef<
  HTMLButtonElement,
  TakeControlProps
>(({ onExited }, ref) => {
  const { formatMessage, locale } = useIntl();
  const [showSuccess, setShowSuccess] = useState(false);
  const { queryParams, redirect, location } = useRouter();
  const [makeDataSubjectRequests, { error }] = useMakeDataSubjectRequests({
    refetchQueries: ['RequestHistory', 'RequestHistoryActiveCount'],
  });
  const [{ selectedPartitionOption, partitionOptions, showPartition }] =
    usePartition();
  const coreIdentifier = useSelector(selectCoreIdentifier);
  const activeDataSubject = useSelector(selectActiveDataSubject);
  const isLoggedIn = useSelector(selectIsLoggedIn);
  const isLoggingIn = useSelector(selectIsLoggingIn);
  const takeControlModalName = useSelector(selectTakeControlModalName);
  const privacyCenter = useSelector(selectRequiredPrivacyCenter);
  const dispatch = useDispatch();
  const authorizationUrlPayload = useAuthorizationUrl(activeDataSubject);

  const { data } = usePrivacyCenterWorkflows();

  const selectedWorkflow = isLoggedIn
    ? (data?.nodes || []).find(
        (workflow) => workflow.id === queryParams.workflowId ?? '',
      )
    : undefined;

  // We need at least one action and one subject
  if (!activeDataSubject) {
    return null;
  }

  const hideModal = (): void => {
    dispatch(unsetActiveDataSubject);
    dispatch(unsetSelectedWorkflowId);
    dispatch(unsetSelectedAction);
    if (showSuccess) {
      redirect(removeQueryParams(location, ['action', 'modal']));
    } else {
      redirect(removeQueryParams(location, ['modal']));
    }
    setShowSuccess(false);
  };

  const title = (
    <TextWrapper>
      {activeDataSubject.disabledInstructions ? (
        <DescriptionWrapper>
          <SanitizeHtml
            html={formatMessage(activeDataSubject.disabledInstructions)}
          />
        </DescriptionWrapper>
      ) : (
        <>
          <TitleWrapper>
            {formatMessage(takeControlMessages.title)}
          </TitleWrapper>
          <DescriptionWrapper>
            <SanitizeHtml
              html={formatMessage(
                privacyCenterCustomizableMessages.takeControlDescription,
              )}
            />
          </DescriptionWrapper>
          <DataSubjectWrapper>
            {isLoggedIn
              ? formatMessage(takeControlMessages.dataSubjectLoggedIn, {
                  dataSubject: (
                    <DataSubjectValueWrapper>
                      {formatMessage(activeDataSubject.title).toLowerCase()}
                    </DataSubjectValueWrapper>
                  ),
                })
              : formatMessage(takeControlMessages.dataSubjectNotLoggedIn, {
                  verifyIdentityLogin: <Login />,
                })}
          </DataSubjectWrapper>
          {showPartition &&
            selectedPartitionOption &&
            partitionOptions.length > 0 && (
              <DataSubjectWrapper>
                {formatMessage(takeControlMessages.partitionLoggedIn, {
                  partition: selectedPartitionOption.label,
                })}
              </DataSubjectWrapper>
            )}
        </>
      )}
    </TextWrapper>
  );

  return (
    <TakeControlWrapper>
      <TakeControlModalWrapper>
        <ClassOverrides />
        {privacyCenter.showPrivacyRequestButton && (
          <ButtonWrapper
            buttonType="primary"
            id={`modal-${takeControlModalName}`}
            onClick={() =>
              redirect(
                addQueryParams(location, { modal: takeControlModalName }),
              )
            }
            ref={ref}
          >
            {formatMessage(takeControlMessages.takeControl)}
          </ButtonWrapper>
        )}
        <StyledModal
          width="fit-content"
          centered
          onHide={hideModal}
          onExited={onExited}
          show={queryParams.modal === 'select-subject'}
          label={takeControlMessages.dataSubjectSelectLabel}
        >
          <SelectSubjectModalWrapper>
            <ActiveDataSubject />
          </SelectSubjectModalWrapper>
        </StyledModal>
        <StyledModal
          width="fit-content"
          centered
          onHide={hideModal}
          onExited={onExited}
          show={activeDataSubject && queryParams.modal === 'take-control'}
          label={takeControlMessages.title}
        >
          {isLoggingIn ? (
            <ModalWrapper>
              {title}
              <Skeleton paragraph={false} />
              <Skeleton paragraph />
              <Skeleton paragraph />
            </ModalWrapper>
          ) : selectedWorkflow ? (
            <TakeControlConfirm
              coreIdentifier={coreIdentifier?.value || ''}
              activeDataSubjectId={activeDataSubject.id}
              goBack={() => {
                setShowSuccess(false);
                redirect(removeQueryParams(location, ['action', 'workflowId']));
              }}
              confirmRequest={({
                captcha,
                subjectType,
                type,
                postCompileStatus,
                region,
                details,
                ...rest
              }) =>
                makeDataSubjectRequests?.({
                  variables: {
                    input: [
                      {
                        type,
                        subjectType,
                        details,
                        region,
                        workflowConfigId: selectedWorkflow.id,
                        ...(postCompileStatus ? { postCompileStatus } : {}),
                        requestIdentifiers: formToRequestIdentifiers(rest),
                        locale: locale as LanguageKey,
                        captcha,
                        partitionKey: selectedPartitionOption?.value,
                      },
                    ],
                  },
                }).then(({ data }) => {
                  if (data) {
                    const { consentManagers } = privacyCenter;
                    consentManagers.forEach(({ id, configuration }) => {
                      if (
                        type === RequestAction.TrackingOptOut &&
                        configuration.syncEndpoint
                      ) {
                        syncAllConsent(configuration.syncEndpoint, id, false);
                      }

                      if (
                        type === RequestAction.SaleOptOut &&
                        configuration.syncEndpoint
                      ) {
                        syncSaleOfInfoConsent(
                          configuration.syncEndpoint,
                          id,
                          false,
                        );
                      }
                    });

                    setShowSuccess(true);
                  }
                })
              }
              subjectType={activeDataSubject.type}
              submittedSuccessfully={showSuccess}
              error={error}
              action={{
                ...selectedWorkflow.action,
                // Inject the form items, for backwards compatibility
                formItems: selectedWorkflow.formItems,
              }}
              workflow={selectedWorkflow}
            />
          ) : (
            <ModalWrapper>
              {title}
              {!activeDataSubject.disabledInstructions && (
                <>
                  <HeaderWrapper>
                    {formatMessage(takeControlMessages.header)}
                  </HeaderWrapper>
                  <DisplayWorkflows
                    workflows={data?.nodes || []}
                    selectWorkflow={(selectedWorkflow) => {
                      // If active data subject has oauth, navigate to OAuth route, otherwise
                      // immediately navigate to process dsr
                      if (authorizationUrlPayload && !isLoggedIn) {
                        dispatch(
                          setSelectedWorkflowId({
                            selectedWorkflowId: selectedWorkflow.id,
                            oAuthState: authorizationUrlPayload.state,
                            selectedLocale: locale as LanguageKey,
                          }),
                        );
                        writeOauthStateToLocalStorage(
                          authorizationUrlPayload.state,
                        );
                        window.location.replace(
                          authorizationUrlPayload.url.href,
                        );
                      } else if (!authorizationUrlPayload && !isLoggedIn) {
                        redirect(
                          addQueryParams(location, {
                            modal: 'log-in',
                            workflowId: selectedWorkflow.id,
                          }),
                        );
                      } else {
                        redirect(
                          addQueryParams(location, {
                            workflowId: selectedWorkflow.id,
                          }),
                        );
                      }
                    }}
                  />
                </>
              )}
            </ModalWrapper>
          )}
        </StyledModal>
      </TakeControlModalWrapper>
    </TakeControlWrapper>
  );
});

export default WorkflowsTakeControl;
